import axios from 'axios';
import type { BoardDetails } from '../interfaces/BoardDetails';
import type { BoardParticipantDetail } from '../interfaces/BoardParticipantDetail';
import type { Idea } from '../interfaces/Idea';
import type { IdeaComment } from '../interfaces/IdeaComment';
import type { IdeaVote } from '../interfaces/IdeaVote';
import type { BoardChat } from '../interfaces/BoardChat';
import { API_BASE_URL, apiRequest, refreshLocalStorageAccessTokenIfNeeded } from './apiClient';
import { isTeamsHost } from './webAuth';
import useUserStore from '../store/userStore';

export type InvitationRateLimitState = {
  limit: number;
  used: number;
  remaining: number;
  resetAt: string | null;
  emailSendingBlocked: boolean;
};

export type InvitationSendResponse = {
  rateLimit: InvitationRateLimitState;
};

interface TopicResponse extends BoardDetails {
  ideas: Idea[];
  chat?: BoardChat[];
}

interface TopicMemberResponse {
  user: BoardParticipantDetail;
}

interface CreateIdeaResponse {
  idea: Idea;
}

type UploadIdeasResponse = Idea[];
type UploadIdeaType = 'image' | 'video' | 'file';
type UpdateUploadResponse = Partial<Idea>;

interface CreateVoteResponse {
  vote: IdeaVote;
}

interface CreateCommentResponse {
  comment: IdeaComment;
}

interface CreateTopicResponse {
  topic: BoardDetails;
}

interface UrlPreviewResponse {
  title: string;
  image: string;
}

interface CreateChatResponse {
  chat: BoardChat;
}

interface TopicRequiresAuthenticationResponse {
  requiresAuthentication: boolean;
  topic?: {
    id: string;
    title: string;
    isPublic: boolean;
  };
}

interface TopicAuthenticateResponse {
  key?: string;
}

interface BoardExportResponse {
  blob: Blob;
  filename: string;
}

export interface BoardTemplate {
  id: string;
  spaceType?: 'board' | 'wall' | 'collage' | 'game';
  sourceBoard: string;
  name: string;
  description: string;
  visibility: 'private' | 'shared' | 'marketplace';
  storageProvider: string;
  contentType: string;
  sizeBytes: number;
  manifestVersion: number;
  cardCount: number;
  assetCount: number;
  templateSettings?: {
    board?: {
      layout?: 'grid' | 'lanes' | 'columns';
      columnCount?: number;
      cardSorting?: 'custom' | 'automatic';
    };
    wall?: {
      enabledTools?: Array<
        'drawing' | 'cards' | 'clustering' | 'resizing' | 'locking' | 'unlocking'
      >;
    };
  } | null;
  created: string;
  lastUpdated?: string;
}

interface SaveBoardTemplateResponse {
  template: BoardTemplate;
}

interface BoardBackgroundResponse {
  image: string;
  mimetype: string;
  repeat: boolean;
}

type BoardTimerResponse = Pick<
  BoardDetails,
  | 'timerStartedAt'
  | 'timerStoppedAt'
  | 'timerDurationSeconds'
  | 'timerStatus'
  | 'timerCompletionActions'
  | 'timerCompletionActionsAppliedAt'
  | 'votingEnabled'
  | 'participantUploadsEnabled'
  | 'commentsEnabled'
>;

export interface DotstormingImportBoard {
  id: string;
  title: string;
}

export interface DotstormingImportCard {
  id: string;
  type: string;
  title?: string;
  content?: string;
  voteCount?: number;
}

interface CreateBoardPayload {
  title: string;
  description: string;
  isPublic: boolean;
  source?: 'default' | 'teams';
  layout: BoardDetails['layout'];
  columns?: BoardDetails['columns'];
  cardSorting: NonNullable<BoardDetails['cardSorting']>;
  votingStyle: BoardDetails['votingStyle'];
  privateVotingEnabled?: boolean;
  votingEnabled: boolean;
  votesPerUser: number;
  participantUploadsEnabled?: boolean;
  timerEnabled?: boolean;
  uploadTypes: string;
  participants: BoardDetails['participants'];
}

const getSessionHeaders = (boardId?: string) => {
  const session = useUserStore.getState().getBoardSession(boardId);
  const headers: Record<string, string> = {};

  if (session.authToken) {
    headers.Authorization = `Bearer ${session.authToken}`;
  }

  const userId = session.user?.id?.trim();

  if (boardId && userId) {
    headers[`dotstorming_topic_user_${boardId}`] = userId;
  }

  const boardKey = session.key?.trim();
  if (boardKey) {
    headers.key = boardKey;
  }

  return headers;
};

const normalizeIdea = (idea: Partial<Idea>, boardId: string): Idea => ({
  ...idea,
  topic: idea.topic?.trim() || boardId,
  ownerId: idea.ownerId ?? '',
  title: idea.title ?? '',
  content: idea.content ?? '',
  thumbnail: idea.thumbnail ?? '',
  type: idea.type ?? 'text',
  approved: idea.approved === false ? false : true,
  mimeType: idea.mimeType ?? '',
  votes: Array.isArray(idea.votes) ? idea.votes : [],
  pinned: idea.pinned === true,
  processingStatus: idea.processingStatus ?? 'processed',
  suggested: idea.suggested ?? '',
  comments: Array.isArray(idea.comments) ? idea.comments : [],
  id: idea.id ?? '',
});

const headersToRecord = (headers: Headers): Record<string, string> => {
  const headerRecord: Record<string, string> = {};
  headers.forEach((value, key) => {
    headerRecord[key] = value;
  });
  return headerRecord;
};

const getErrorMessage = (status: number, payload: unknown) => {
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }
  if (payload && typeof payload === 'object' && 'message' in payload) {
    const message = (payload as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return `Request failed with status ${status}`;
};

export const dotvotingApi = {
  getRealtimeToken: (boardId: string) => apiRequest<{ token: string }>(
    '/realtime/token',
    { method: 'POST', body: JSON.stringify({ kind: 'dotvoting', id: boardId }) },
    getSessionHeaders(boardId)
  ),
  setSession(boardId: string, options?: { authToken?: string; isGuest?: boolean }) {
    useUserStore.getState().setBoardSession(boardId, options);
  },

  getSharingVerification() {
    return apiRequest<{ user: { emailVerified?: boolean } }>(
      '/users',
      undefined,
      getSessionHeaders()
    );
  },

  requestEmailVerification() {
    return apiRequest<{ emailVerified?: boolean }>(
      '/users/email-verification/request',
      { method: 'POST', body: '{}' },
      getSessionHeaders()
    );
  },

  async getBoard(boardId: string): Promise<TopicResponse> {
    const board = await apiRequest<TopicResponse>(
      `/topics/${boardId}`,
      undefined,
      getSessionHeaders(boardId)
    );

    return {
      ...board,
      ideas: Array.isArray(board.ideas)
        ? board.ideas.map((idea) => normalizeIdea(idea, boardId))
        : [],
    };
  },

  async getBoardRequiresAuthentication(action: 'topic' | 'join', boardId: string) {
    return apiRequest<TopicRequiresAuthenticationResponse>(
      `/topics/password/${action}/${boardId}`,
      undefined,
      getSessionHeaders(boardId)
    );
  },

  async authenticateBoard(boardId: string, password: string) {
    const payload = await apiRequest<TopicAuthenticateResponse>(
      `/topics/password/${boardId}`,
      {
        method: 'POST',
        body: JSON.stringify({ password }),
      },
      getSessionHeaders(boardId)
    );

    const key = payload?.key?.trim() ?? null;
    useUserStore.getState().setBoardSessionKey(key);

    return key;
  },

  async ensureMember(boardId: string, participants: BoardParticipantDetail[]) {
    if (!isTeamsHost()) {
      return null;
    }

    const session = useUserStore.getState().getBoardSession(boardId);
    const participantName = useUserStore.getState().getCurrentUser()?.name?.trim() ?? '';

    if (session.user?.id) {
      const existingParticipant =
        participants.find((participant) => participant._id === session.user?.id) ?? null;

      if (existingParticipant) {
        return existingParticipant;
      }
    }

    if (!participantName) {
      return null;
    }

    try {
      const payload = await apiRequest<TopicMemberResponse>(
        `/topics/member/${boardId}`,
        {
          method: 'POST',
          body: JSON.stringify({ name: participantName }),
        },
        getSessionHeaders(boardId)
      );

      return payload.user;
    } catch {
      return null;
    }
  },

  async joinMember(boardId: string, name: string) {
    const payload = await apiRequest<TopicMemberResponse>(
      `/topics/member/${boardId}`,
      {
        method: 'POST',
        body: JSON.stringify({ name }),
      },
      getSessionHeaders(boardId)
    );

    return payload.user;
  },

  async deleteMember(boardId: string, participantIds: string) {
    await apiRequest<void>(
      `/topics/participant/${boardId}/${participantIds}`,
      { method: 'DELETE' },
      getSessionHeaders(boardId)
    );
  },

  async createBoard(authToken: string, board: CreateBoardPayload) {
    const payload = await apiRequest<CreateTopicResponse>(
      '/topics',
      {
        method: 'POST',
        body: JSON.stringify(board),
      },
      { Authorization: `Bearer ${authToken}` }
    );

    return payload.topic;
  },

  async createIdea(boardId: string, content: string, options?: { columnId?: string }) {
    const payload = await apiRequest<CreateIdeaResponse>(
      `/boardIdeas/${boardId}`,
      {
        method: 'POST',
        body: JSON.stringify({ content, columnId: options?.columnId }),
      },
      getSessionHeaders(boardId)
    );

    return normalizeIdea(payload.idea, boardId);
  },

  async getIdea(boardId: string, ideaId: string) {
    const payload = await apiRequest<CreateIdeaResponse>(
      `/boardIdeas/${boardId}/${ideaId}`,
      undefined,
      getSessionHeaders(boardId)
    );

    return normalizeIdea(payload.idea, boardId);
  },
  async uploadAttachmentIdea(
    boardId: string,
    file: File,
    uploadType: UploadIdeaType,
    title?: string,
    options?: { columnId?: string }
  ) {
    const formData = new FormData();
    if (options?.columnId) {
      formData.append('columnId', options.columnId);
    }
    formData.append('file', file);
    const requestHeaders = new Headers(getSessionHeaders(boardId));
    await refreshLocalStorageAccessTokenIfNeeded(requestHeaders);
    const axiosHeaders = headersToRecord(requestHeaders);

    let payload: UploadIdeasResponse;
    try {
      const response = await axios.post<UploadIdeasResponse>(
        `${API_BASE_URL}/boardIdeas/upload/${boardId}/${uploadType}`,
        formData,
        {
          headers: axiosHeaders,
        }
      );
      payload = response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(getErrorMessage(error.response.status, error.response.data));
      }
      throw error;
    }
    const uploadedIdeas = payload.map((idea) => normalizeIdea(idea, boardId));
    const trimmedTitle = title?.trim() ?? '';
    const fallbackTitle = uploadType === 'file' ? file.name.trim() : '';
    const titleToApply = trimmedTitle || fallbackTitle;

    if (!titleToApply) {
      return uploadedIdeas;
    }

    await Promise.all(
      uploadedIdeas.map((idea) =>
        apiRequest<void>(
          `/boardIdeas/upload/${boardId}/${idea.id}`,
          {
            method: 'PUT',
            body: JSON.stringify({ title: titleToApply }),
          },
          getSessionHeaders(boardId)
        )
      )
    );

    return uploadedIdeas.map((idea) => ({ ...idea, title: titleToApply }));
  },

  async uploadImageIdea(
    boardId: string,
    file: File,
    title?: string,
    options?: { columnId?: string }
  ) {
    return this.uploadAttachmentIdea(boardId, file, 'image', title, options);
  },

  async uploadVideoIdea(
    boardId: string,
    file: File,
    title?: string,
    options?: { columnId?: string }
  ) {
    return this.uploadAttachmentIdea(boardId, file, 'video', title, options);
  },

  async uploadFileIdea(
    boardId: string,
    file: File,
    title?: string,
    options?: { columnId?: string }
  ) {
    return this.uploadAttachmentIdea(boardId, file, 'file', title, options);
  },

  async getIdeaImageBlob(boardId: string, ideaId: string) {
    const requestHeaders = new Headers(getSessionHeaders(boardId));
    await refreshLocalStorageAccessTokenIfNeeded(requestHeaders);
    const response = await axios.get<Blob>(
      `${API_BASE_URL}/boardIdeas/image/${boardId}/${ideaId}`,
      {
        headers: headersToRecord(requestHeaders),
        responseType: 'blob',
      }
    );

    return response.data;
  },

  async updateImageIdea(
    boardId: string,
    ideaId: string,
    file: Blob,
    title?: string,
    mimeType?: string
  ) {
    const formData = new FormData();
    const imageType = mimeType ?? file.type;
    formData.append('title', title ?? '');
    formData.append('file', file, `rotated-image.${imageType.split('/')[1] || 'png'}`);

    const requestHeaders = new Headers(getSessionHeaders(boardId));
    await refreshLocalStorageAccessTokenIfNeeded(requestHeaders);

    let payload: UpdateUploadResponse;
    try {
      const response = await axios.put<UpdateUploadResponse>(
        `${API_BASE_URL}/boardIdeas/upload/${boardId}/${ideaId}`,
        formData,
        {
          headers: headersToRecord(requestHeaders),
        }
      );
      payload = response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(getErrorMessage(error.response.status, error.response.data));
      }
      throw error;
    }

    return normalizeIdea({ id: ideaId, ...payload }, boardId);
  },

  async setPinned(boardId: string, ideaId: string, pinned: boolean) {
    await apiRequest<void>(
      `/boardIdeas/${boardId}/${ideaId}/pinned`,
      {
        method: 'PUT',
        body: JSON.stringify({ pinned }),
      },
      getSessionHeaders(boardId)
    );
  },

  async setIdeaColumn(boardId: string, ideaId: string, columnId: string) {
    await apiRequest<void>(
      `/boardIdeas/${boardId}/${ideaId}/column`,
      {
        method: 'PUT',
        body: JSON.stringify({ columnId }),
      },
      getSessionHeaders(boardId)
    );
  },

  async reorderIdeaSortOrder(boardId: string, cardOrder: string[]) {
    await apiRequest<void>(
      `/boardIdeas/${boardId}/order`,
      {
        method: 'PUT',
        body: JSON.stringify({ cardOrder }),
      },
      getSessionHeaders(boardId)
    );
  },

  async approveIdea(boardId: string, ideaId: string) {
    await apiRequest<void>(
      `/boardIdeas/${boardId}/${ideaId}/approve`,
      { method: 'PUT' },
      getSessionHeaders(boardId)
    );
  },

  async updateIdea(boardId: string, ideaId: string, updates: { content?: string; title?: string }) {
    const body: { content?: string; title?: string } = {};

    if (typeof updates.content === 'string') {
      body.content = updates.content;
    }

    if (typeof updates.title === 'string') {
      body.title = updates.title;
    }

    if (Object.keys(body).length === 0) {
      return;
    }

    await apiRequest<void>(
      `/boardIdeas/${boardId}/${ideaId}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
      getSessionHeaders(boardId)
    );
  },

  async deleteIdea(boardId: string, ideaId: string) {
    await apiRequest<void>(
      `/boardIdeas/${boardId}/${ideaId}`,
      { method: 'DELETE' },
      getSessionHeaders(boardId)
    );
  },

  async addVote(boardId: string, ideaId: string) {
    const payload = await apiRequest<CreateVoteResponse>(
      `/votes/${boardId}/${ideaId}`,
      { method: 'POST' },
      getSessionHeaders(boardId)
    );

    return payload.vote;
  },

  async removeVote(boardId: string, ideaId: string, voteId: string) {
    await apiRequest<void>(
      `/votes/${boardId}/${ideaId}/${voteId}`,
      { method: 'DELETE' },
      getSessionHeaders(boardId)
    );
  },

  async addComment(boardId: string, ideaId: string, content: string) {
    const payload = await apiRequest<CreateCommentResponse>(
      `/comments/${boardId}/${ideaId}`,
      {
        method: 'POST',
        body: JSON.stringify({ content }),
      },
      getSessionHeaders(boardId)
    );

    return payload.comment;
  },

  async deleteComment(boardId: string, ideaId: string, commentId: string) {
    await apiRequest<void>(
      `/comments/${boardId}/${ideaId}/${commentId}`,
      { method: 'DELETE' },
      getSessionHeaders(boardId)
    );
  },

  async updateBoard(boardId: string, board: BoardDetails) {
    return apiRequest<BoardDetails>(
      `/topics/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          isPublic: board.isPublic,
          title: board.title,
          description: board.description,
          layout: board.layout,
          columns: board.columns ?? [],
          cardSorting: board.cardSorting,
          votingStyle: board.votingStyle,
          votesPerUser: board.votesPerUser,
          votingEnabled: board.votingEnabled,
          privateVotingEnabled: board.privateVotingEnabled,
          commentsEnabled: board.commentsEnabled,
          giphyEnabled: board.giphyEnabled,
          cardsRequireApproval: board.cardsRequireApproval,
          randomizeCardPosition: board.randomizeCardPosition,
          viewParticipantNames: board.viewParticipantNames,
          allViewParticipantNames: board.allViewParticipantNames,
          chatEnabled: board.chatEnabled,
          timerEnabled: board.timerEnabled,
          participantUploadsEnabled: board.participantUploadsEnabled,
          uploadTypes: board.uploadTypes,
          participants: board.participants,
        }),
      },
      getSessionHeaders(boardId)
    );
  },

  async setPrivateVotingEnabled(boardId: string, enabled: boolean) {
    await apiRequest<void>(
      `/topics/privateVoting/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      },
      getSessionHeaders(boardId)
    );
  },

  async setVotingEnabled(boardId: string, enabled: boolean) {
    await apiRequest<void>(
      `/topics/voting/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      },
      getSessionHeaders(boardId)
    );
  },

  async setCommentingEnabled(boardId: string, enabled: boolean) {
    await apiRequest<void>(
      `/topics/commenting/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      },
      getSessionHeaders(boardId)
    );
  },

  async setChatEnabled(boardId: string, enabled: boolean) {
    await apiRequest<void>(
      `/topics/chatEnabled/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      },
      getSessionHeaders(boardId)
    );
  },

  async setParticipantsEnabled(boardId: string, enabled: boolean) {
    await apiRequest<void>(
      `/topics/enableParticipants/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      },
      getSessionHeaders(boardId)
    );
  },

  async setGiphyEnabled(boardId: string, enabled: boolean) {
    await apiRequest<void>(
      `/topics/giphyEnabled/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      },
      getSessionHeaders(boardId)
    );
  },

  async setParticipantNameVisibility(boardId: string, status: 'none' | 'owner' | 'all') {
    await apiRequest<void>(
      `/topics/participantNames/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ status }),
      },
      getSessionHeaders(boardId)
    );
  },

  async setLocked(boardId: string, locked: boolean) {
    await apiRequest<void>(
      `/topics/locked/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ locked }),
      },
      getSessionHeaders(boardId)
    );
  },

  async setBoardTimer(
    boardId: string,
    action: 'start' | 'stop' | 'resume' | 'reset' | 'complete',
    durationSeconds?: number,
    completionActions?: BoardDetails['timerCompletionActions']
  ) {
    return apiRequest<BoardTimerResponse>(
      `/topics/timer/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          action,
          ...(typeof durationSeconds === 'number' ? { durationSeconds } : {}),
          ...(completionActions ? { completionActions } : {}),
        }),
      },
      getSessionHeaders(boardId)
    );
  },

  async getBoardBackground(boardId: string) {
    const requestHeaders = new Headers(getSessionHeaders(boardId));
    await refreshLocalStorageAccessTokenIfNeeded(requestHeaders);
    const axiosHeaders = headersToRecord(requestHeaders);

    try {
      const response = await axios.get<Blob>(`${API_BASE_URL}/topics/background/${boardId}`, {
        headers: axiosHeaders,
        responseType: 'blob',
      });

      return window.URL.createObjectURL(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(getErrorMessage(error.response.status, error.response.data));
      }
      throw error;
    }
  },

  async setBoardBackground(boardId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const requestHeaders = new Headers(getSessionHeaders(boardId));
    await refreshLocalStorageAccessTokenIfNeeded(requestHeaders);
    const axiosHeaders = headersToRecord(requestHeaders);

    try {
      await axios.post(`${API_BASE_URL}/topics/background/${boardId}`, formData, {
        headers: axiosHeaders,
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(getErrorMessage(error.response.status, error.response.data));
      }
      throw error;
    }
  },

  async setBoardBackgroundRepeat(boardId: string, repeat: boolean) {
    return apiRequest<BoardBackgroundResponse>(
      `/topics/background/${boardId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ repeat }),
      },
      getSessionHeaders(boardId)
    );
  },

  async clearAll(boardId: string) {
    await apiRequest<void>(
      `/topics/clear/all/${boardId}`,
      { method: 'DELETE' },
      getSessionHeaders(boardId)
    );
  },

  async clearVotes(boardId: string) {
    await apiRequest<void>(
      `/topics/clear/votes/${boardId}`,
      { method: 'DELETE' },
      getSessionHeaders(boardId)
    );
  },

  async exportBoard(boardId: string, ownerId: string): Promise<BoardExportResponse> {
    const requestHeaders = new Headers(getSessionHeaders(boardId));
    await refreshLocalStorageAccessTokenIfNeeded(requestHeaders);
    const axiosHeaders = headersToRecord(requestHeaders);

    try {
      const response = await axios.get<Blob>(
        `${API_BASE_URL}/topics/export/${boardId}/${ownerId}`,
        {
          headers: axiosHeaders,
          responseType: 'blob',
        }
      );
      const disposition =
        typeof response.headers['content-disposition'] === 'string'
          ? response.headers['content-disposition']
          : '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ??
        disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1])
        : 'dotstorming.xlsx';

      return {
        blob: response.data,
        filename,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(getErrorMessage(error.response.status, error.response.data));
      }
      throw error;
    }
  },

  async saveBoardTemplate(boardId: string, input: { name: string; description: string }) {
    const payload = await apiRequest<SaveBoardTemplateResponse>(
      `/templates/from-board/${boardId}`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      getSessionHeaders(boardId)
    );

    return payload.template;
  },

  async getBoardQrCode(boardId: string) {
    return apiRequest<string>(`/qr/board/${boardId}`, undefined, getSessionHeaders(boardId));
  },

  async sendInvitations(boardId: string, emailAddresses: string, emailMessage: string) {
    return apiRequest<InvitationSendResponse>(
      `/topics/invite/${boardId}`,
      {
        method: 'POST',
        body: JSON.stringify({ emailAddresses, emailMessage }),
      },
      getSessionHeaders(boardId)
    );
  },

  async getInvitationRateLimit() {
    return apiRequest<InvitationRateLimitState>(
      '/email-invitations/rate-limit',
      undefined,
      getSessionHeaders()
    );
  },

  async sendFeedback(comment: string, source: 'board' | 'main' = 'board') {
    await apiRequest<void>(
      '/sendFeedback',
      {
        method: 'POST',
        body: JSON.stringify({ comment, source }),
      },
      getSessionHeaders()
    );
  },

  async getImportableDotstormingBoards() {
    return apiRequest<DotstormingImportBoard[]>(
      '/dotstorming/getBoards',
      undefined,
      getSessionHeaders()
    );
  },

  async getImportableDotstormingCards(boardId: string) {
    return apiRequest<DotstormingImportCard[]>(
      `/dotstorming/getCards/${boardId}`,
      undefined,
      getSessionHeaders()
    );
  },

  async importDotstormingCards(targetBoardId: string, sourceBoardId: string, cardIds?: string[]) {
    await apiRequest<void>(
      `/dotstorming/import/${targetBoardId}`,
      {
        method: 'POST',
        body: JSON.stringify({
          sourceBoardId,
          target: 'board',
          ...(cardIds && cardIds.length > 0 ? { cardIds } : {}),
        }),
      },
      getSessionHeaders(targetBoardId)
    );
  },

  async importTxtCsvCards(targetBoardId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const requestHeaders = new Headers(getSessionHeaders(targetBoardId));
    await refreshLocalStorageAccessTokenIfNeeded(requestHeaders);
    const axiosHeaders = headersToRecord(requestHeaders);

    try {
      await axios.post(`${API_BASE_URL}/txtcsv/import/${targetBoardId}?target=board`, formData, {
        headers: axiosHeaders,
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(getErrorMessage(error.response.status, error.response.data));
      }
      throw error;
    }
  },

  async getUrlPreview(boardId: string, url: string): Promise<UrlPreviewResponse> {
    return apiRequest<UrlPreviewResponse>(
      `/boardIdeas/url/${boardId}`,
      {
        method: 'POST',
        body: JSON.stringify({ url }),
      },
      getSessionHeaders(boardId)
    );
  },

  async addChat(boardId: string, username: string, content: string) {
    const payload = await apiRequest<CreateChatResponse>(
      `/topics/chat/${boardId}`,
      {
        method: 'POST',
        body: JSON.stringify({ username, content }),
      },
      getSessionHeaders(boardId)
    );

    return payload.chat;
  },
};
